// Metric Beaming:
// takes a measure of notes & rests, and breaks them into beam groups
// to be beamed by vexFlow.
//
// This works according to the meter -- unlike VexFlow's more primitive
// autobeaming routines. GCR TODO: Long term, convert this directly to
// VexFlow and push upstream.

import Fraction from './fraction';
// eslint-disable-next-line no-unused-vars
import type NoteHolder from './noteHolder';
import type Measure from './measure';
import type TimeSignature from './timeSignature';

export interface NoteHolderInMeasurePart {
  holder: NoteHolder;
  pointerFraction: Fraction;
  isBeamable?: boolean;
  partialDirection?: 'left' | 'right';
}

// GCR TODO: Beam groups should take into account tuplets as well...
const debug = false;
const log = (s) => {
  if (debug) console.log(s);
};

/**
 * apply beams to a measure of music
 * @param  {Measure} measure
 * @param  {TimeSignature} timeSignature [description]
 * @return {NoteHolders[]}
 */
export default (
  measure: Measure,
  timeSignature: TimeSignature,
  beamRests = false
): NoteHolderInMeasurePart[][] => {
  const measureParts = getMeasureParts(measure, timeSignature);
  log('measureParts');
  log(measureParts);
  let activeGroup: NoteHolderInMeasurePart[] = [];
  const beamGroups: NoteHolderInMeasurePart[][] = [];
  const newGroup = () => {
    log('new group');
    // If there are more than two notes, save it as a beam group:
    if (activeGroup.length >= 2) {
      calculateSingletonDirections(activeGroup);
      beamGroups.push(activeGroup);
    }

    // Start a new active group:
    activeGroup = [];
  };

  // For each measurePart
  for (let i = 0; i < measureParts.length; i++) {
    const part = measureParts[i];
    // Can't have a beam if only one beamable...
    // eslint-disable-next-line no-continue
    if (part.length < 2) continue;

    const restsBeamable =
      beamRests && part[0].holder.isBeamable() && part[part.length - 1].holder.isBeamable();

    part.forEach((note) => {
      note.isBeamable =
        note.holder.isBeamable() || (restsBeamable && note.holder.rhythm.isBeamable());
    });

    // Loop through notes within each beaming Fraction
    for (let j = 0; j < part.length; j++) {
      const priorNote = part[j - 1];
      const note = part[j];
      const nextNote = part[j + 1];

      // console.log(`${note.holder.toString()} is beamable? ${note.isBeamable}`);

      // Is it beamable?
      if (
        // 8th note or smaller
        note.isBeamable &&
        // Followed or preceeded by beamable note within
        // the same group?
        ((nextNote && nextNote.isBeamable) || (priorNote && priorNote.isBeamable))
      ) {
        log('adding to the group...');
        // Add it to the beamGroup!
        activeGroup.push(note);
      } else {
        log('new group: ');
        log(activeGroup);
        // Otherwise, start a new beam group:
        newGroup();
      }
      if (!nextNote) newGroup();
    }
  }
  return beamGroups;
};

// Helper functions:
function calculateSingletonDirections(group: NoteHolderInMeasurePart[]) {
  // For each note:
  for (let i = 0; i < group.length; i++) {
    const priorNote = group[i - 1];
    const note = group[i];
    const nextNote = group[i + 1];

    // Is it a singleton (aka partialBeam in vex terminology)?
    const singleton =
      // It has more than the previous note's beams:
      priorNote &&
      nextNote &&
      priorNote.holder.numberOfBeams() < note.holder.numberOfBeams() &&
      // And it has more than the next note's beams:
      nextNote.holder.numberOfBeams() < note.holder.numberOfBeams();

    if (singleton) {
      // Should it's flag point right?
      // Does its startPointer modulo its parent rhythm (twice as long)
      // equal zero? If so -- it's at the start of its subdivisional unit
      // and should point to the right. Otherwise, it should point to the
      // left.
      log(
        `${
          note.pointerFraction
        } % (${note.holder.rhythm.getFraction()} * 2) = ${note.pointerFraction
          .clone()
          .modulo(note.holder.rhythm.getFraction().multiply(2))
          .equals(0)}`
      );
      if (
        note.pointerFraction.clone().modulo(note.holder.rhythm.getFraction().multiply(2)).equals(0)
      ) {
        log('Forcing partial right: ');
        log(note);
        note.partialDirection = 'right';
      } else {
        log('Forcing partial left: ');
        log(note);
        note.partialDirection = 'left';
      }
      if (note.holder.forcePartialBeamDirection) {
        note.partialDirection = note.holder.forcePartialBeamDirection;
        log(`Manual direction override: ${note.holder.forcePartialBeamDirection}`);
      }
    }
  }
}

/**
 * Returns noteHolders grouped into metrically beamable chunks -- though makes no
 * promises about whether things can be beamed within each group.
 * @param {Measure} measure
 * @param {TimeSignature} timeSignature
 * @returns {NoteHolder[][]}
 */
function getMeasureParts(measure: Measure, timeSignature: TimeSignature) {
  const noteHolders = measure.getNoteHolders();

  // Divide into measure parts following beaming fraction:
  /** The starting fraction location within the measure of the currently processing note */
  const pointerFraction = new Fraction(0, 1);

  /** The starting fraction location within the measure of the currently processing beam */
  let groupPointerFraction = new Fraction(0, 1);

  const measureParts: NoteHolderInMeasurePart[][] = [];

  // const beamPoints = [new Fraction(0, 1)];
  const beamDurations = measure.beamDurations || timeSignature.beamDurations;
  /** Fractions representing the duration of beams in a measure */
  const beamFractions = beamDurations.map((dur) => dur.getFraction());

  /** The fractions at which beams should begin or end in a measure: */
  const beamPoints = [new Fraction(0, 1)];

  /** We repeat the beamDurations 20 times -- so that if somone has more than the
   * length of a measure in a measure it will continue to beam in a similar way.
   * (For instance: complete the measure.) Additionally, in a measure like
   * 4/4, timeSignature.beamDurations = ['q'], so we need to repeat it.
   */
  for (let totalCounter = 0; totalCounter < 20; totalCounter++) {
    const lastFract = beamPoints[beamPoints.length - 1];
    const beamFract = beamFractions[totalCounter % beamFractions.length];
    beamPoints.push(lastFract.clone().add(beamFract));
  }

  function isBeamStart() {
    const index = beamPoints.findIndex((f) => f.equals(pointerFraction));
    if (index !== -1) return true;
    return false;
  }

  function getCurrentBeamFraction() {
    const index = beamPoints.findIndex((f) => f.greaterThan(pointerFraction)) - 1;
    const duration = beamDurations[index % beamDurations.length].getFraction().clone();
    return duration;
  }

  function getNextBeamPoint() {
    return beamPoints.find((f) => f.greaterThan(pointerFraction));
  }

  /** The current beam group */
  let currentGroup: NoteHolderInMeasurePart[] = [];

  noteHolders.forEach((noteHolder) => {
    const rhythmFraction = noteHolder.rhythm.getFraction();

    // The current group has one note already
    const currentGroupHasAtLeastOneNote = currentGroup.length > 0;

    // At the start of a beat:
    const atBeamStartPoint = isBeamStart();

    // Adding the note would make the current group bigger
    // than a beaming fraction, so we better start a new group
    const noSpaceForThisNote = groupPointerFraction
      .clone()
      .add(rhythmFraction)
      .greaterThan(getCurrentBeamFraction());

    // The note would cross a beam point, so we better start a new group
    const thisNoteCrossesBeamPoint = pointerFraction
      .clone()
      .add(rhythmFraction)
      .greaterThan(getNextBeamPoint());

    const newGroupNeeded =
      currentGroupHasAtLeastOneNote &&
      (atBeamStartPoint || noSpaceForThisNote || thisNoteCrossesBeamPoint);

    if (newGroupNeeded) {
      // Terminate the current group and begin a new one:
      measureParts.push(currentGroup);
      currentGroup = [];
      groupPointerFraction = new Fraction(0, 1);
    }
    // Add the current note to the current group & update the pointer:
    currentGroup.push({
      holder: noteHolder,
      pointerFraction: pointerFraction.clone(),
    });
    pointerFraction.add(rhythmFraction);
    groupPointerFraction.add(rhythmFraction);
  });

  // Add the last group:
  measureParts.push(currentGroup);

  log('measureParts');
  log(measureParts);
  return measureParts;
}
